import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { useIsCommunicationAdmin } from '../hooks/useIsCommunicationAdmin';
import { TimeTrackingAdmin } from '../components/TimeTrackingAdmin';
import { PageLayout } from '../../../components/common/PageLayout';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { formatDateTimeLocal, formatDateTimeET, formatPhoneNumber } from '../format';
import { ui } from '../ui';
import {
  Agent, Category, Call, CurrentAgent, ZoomUser, JcsaEmployee, MissedCallMetrics,
  CommunicationSettings, ZoomQueuePreview,
  getMe, getAgents, deactivateAgent, reactivateAgent, setAgentZoom, setAgentJcsaEmployee, getJcsaEmployees, getZoomUsers,
  getCategories, deactivateCategory,
  getAllCalls, getUnmappedCalls, assignCall, simulateCall, getMissedMetrics,
  getSettings, updateSettings, previewZoomQueue,
} from '../api';

// How often the live tabs (Calls, Metrics) re-fetch so newly-ingested Zoom calls appear
// without a manual reload. A manual Refresh button is always available too.
const POLL_MS = 20_000;

type TabId = 'agents' | 'calls' | 'categories' | 'metrics' | 'timetracking' | 'settings';
const TABS: { id: TabId; label: string }[] = [
  { id: 'agents', label: 'Agents' },
  { id: 'calls', label: 'Calls' },
  { id: 'categories', label: 'Categories' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'timetracking', label: 'Time Tracking' },
  { id: 'settings', label: 'Settings' },
];

// Rows per page for the Calls tab tables.
const CALLS_PAGE_SIZE = 25;

// ── Metrics date-range presets ────────────────────────────────────────────────
// Ranges are US Eastern calendar dates (the admins' reporting timezone) and weeks
// run Sunday–Saturday. Custom exposes explicit start/end date fields.
type MetricsPreset = 'current-week' | 'prior-week' | 'current-month' | 'prior-month' | 'custom';
const METRICS_PRESETS: { id: MetricsPreset; label: string }[] = [
  { id: 'current-week', label: 'Current Week' },
  { id: 'prior-week', label: 'Prior Week' },
  { id: 'current-month', label: 'Current Month' },
  { id: 'prior-month', label: 'Prior Month' },
  { id: 'custom', label: 'Custom' },
];

// Today's calendar date in ET as YYYY-MM-DD (the en-CA locale formats ISO-style).
const etToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
// Date-only arithmetic in UTC-midnight space so the viewer's own timezone can't shift a day.
const isoToDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
const dateToIso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => dateToIso(new Date(isoToDate(iso).getTime() + n * 86_400_000));
const fmtRangeDate = (iso: string) =>
  isoToDate(iso).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

function presetRange(preset: MetricsPreset): { from: string; to: string } | null {
  const today = etToday();
  const d = isoToDate(today);
  switch (preset) {
    case 'current-week': {
      const sunday = addDays(today, -d.getUTCDay());
      return { from: sunday, to: addDays(sunday, 6) };
    }
    case 'prior-week': {
      const sunday = addDays(today, -d.getUTCDay() - 7);
      return { from: sunday, to: addDays(sunday, 6) };
    }
    case 'current-month':
      return {
        from: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))),
        to: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))),
      };
    case 'prior-month':
      return {
        from: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1))),
        to: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0))),
      };
    default:
      return null;
  }
}

// Prev/Next pager shown under a paginated table; hidden when everything fits on one page.
const Pager: React.FC<{ page: number; total: number; pageSize: number; onPage: (p: number) => void }> = ({
  page, total, pageSize, onPage,
}) => {
  if (total <= pageSize) return null;
  const pages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-muted">
      <span>{from}–{to} of {total}</span>
      <div className="flex items-center gap-2">
        <button className={ui.btnSecondary} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
        <span>Page {page} of {pages}</span>
        <button className={ui.btnSecondary} disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

export const AdminPage: React.FC = () => {
  const isAdmin = useIsCommunicationAdmin();
  const [tab, setTab] = useState<TabId>('agents');
  const [me, setMe] = useState<CurrentAgent | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [zoomUsers, setZoomUsers] = useState<ZoomUser[]>([]);
  const [jcsaEmployees, setJcsaEmployees] = useState<JcsaEmployee[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);
  const [unmapped, setUnmapped] = useState<Call[]>([]);
  const [metrics, setMetrics] = useState<MissedCallMetrics | null>(null);

  // Metrics date-range filter. Admins usually review a week (Sun–Sat) at a time.
  const [metricsPreset, setMetricsPreset] = useState<MetricsPreset>('current-week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Effective range: preset-derived, or the custom fields once both are valid.
  const metricsRange = metricsPreset === 'custom'
    ? (customFrom && customTo && customFrom <= customTo ? { from: customFrom, to: customTo } : null)
    : presetRange(metricsPreset);

  // Per-row state for unassigned call assignment
  const [assignSelections, setAssignSelections] = useState<Record<number, number>>({});

  // Current page for the Calls tab tables (client-side pagination over the already-loaded lists).
  const [callsPage, setCallsPage] = useState(1);
  const [unmappedPage, setUnmappedPage] = useState(1);

  // Staged Zoom-pairing / JCSA-employee edits for the Agents table. Dropdown changes are held
  // here (not persisted) until the user clicks Save, which writes every changed row at once.
  const [pendingAgentEdits, setPendingAgentEdits] = useState<Record<number, { zoom?: string; jcsa?: string }>>({});
  const [savingAgents, setSavingAgents] = useState(false);

  // Dev simulator state
  const [simCallerNumber, setSimCallerNumber] = useState('');
  const [simAgentId, setSimAgentId] = useState<number | ''>('');
  const [simMissed, setSimMissed] = useState(false);
  const [simBusy, setSimBusy] = useState(false);

  // Settings state
  const [settings, setSettings] = useState<CommunicationSettings | null>(null);
  const [extensionInput, setExtensionInput] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<ZoomQueuePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const refreshAgents = () => getAgents().then(setAgents);
  const refreshZoomUsers = () => getZoomUsers().then(setZoomUsers);
  const refreshJcsaEmployees = () => getJcsaEmployees().then(setJcsaEmployees);
  const refreshCalls = () => getAllCalls().then(setCalls);
  const refreshUnmapped = () => getUnmappedCalls().then(setUnmapped);
  // No-op while Custom is selected but incomplete — keeps the last-loaded data on screen.
  const refreshMetrics = () => metricsRange
    ? getMissedMetrics(metricsRange.from, metricsRange.to).then(setMetrics)
    : Promise.resolve();

  const loadSettings = () => getSettings().then(s => {
    setSettings(s);
    setExtensionInput(s.ZoomCallQueueExtension ?? '');
  });

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    getMe().then(setMe);
    refreshAgents();
    refreshZoomUsers();
    refreshJcsaEmployees();
    getCategories().then(setCategories);
    refreshCalls();
    refreshUnmapped();
    loadSettings();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Metrics load and re-load whenever the selected date range changes (preset switch,
  // or a completed pair of custom dates). Also covers the initial load.
  useEffect(() => {
    if (!isAdmin || !metricsRange) return;
    refreshMetrics();
  }, [isAdmin, metricsRange?.from, metricsRange?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch the data behind the currently-selected tab. Used by both the auto-refresh
  // poll and the manual Refresh button, so the two never drift apart.
  const refreshActiveTab = useCallback(async () => {
    switch (tab) {
      case 'calls': await Promise.all([refreshCalls(), refreshUnmapped()]); break;
      case 'metrics': await refreshMetrics(); break;
      case 'agents': await Promise.all([refreshAgents(), refreshZoomUsers()]); break;
      default: break;
    }
  }, [tab, metricsRange?.from, metricsRange?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try { await refreshActiveTab(); } finally { setRefreshing(false); }
  };

  // Auto-refresh the live tabs (Calls, Metrics) on an interval so ingested Zoom calls
  // surface without leaving the page. Static tabs (Categories, Settings) don't poll.
  useEffect(() => {
    if (!isAdmin || (tab !== 'calls' && tab !== 'metrics')) return;
    const id = setInterval(() => { refreshActiveTab(); }, POLL_MS);
    return () => clearInterval(id);
  }, [isAdmin, tab, refreshActiveTab]);

  // Keep the current page in range when a table's row count shrinks (e.g. a call gets assigned).
  useEffect(() => {
    setCallsPage(p => Math.min(p, Math.max(1, Math.ceil(calls.length / CALLS_PAGE_SIZE))));
  }, [calls.length]);
  useEffect(() => {
    setUnmappedPage(p => Math.min(p, Math.max(1, Math.ceil(unmapped.length / CALLS_PAGE_SIZE))));
  }, [unmapped.length]);

  if (!isAdmin) return <Navigate to="/communication" replace />;

  const navItems = [
    { path: '/communication', label: 'My Calls' },
    { path: '/communication/admin', label: 'Admin' },
  ];
  const activeAgents = agents.filter(a => a.IsActive);

  // Current page slices for the Calls tab tables.
  const pagedCalls = calls.slice((callsPage - 1) * CALLS_PAGE_SIZE, callsPage * CALLS_PAGE_SIZE);
  const pagedUnmapped = unmapped.slice((unmappedPage - 1) * CALLS_PAGE_SIZE, unmappedPage * CALLS_PAGE_SIZE);

  // The persisted select value for each column. Zoom: the paired user id ('' = unassigned).
  // JCSA: '' when explicitly unassigned, otherwise the manual override or the email auto-match.
  const currentZoomVal = (a: Agent) => a.ZoomUserId ?? '';
  const currentJcsaVal = (a: Agent) =>
    a.JcsaEmployeeUnassigned ? '' : String(a.JcsaEmployeeID ?? a.MatchedJcsaEmployeeID ?? '');

  // The value shown in each dropdown: the staged edit if the row has one, else the saved value.
  const effectiveZoomVal = (a: Agent) => pendingAgentEdits[a.AgentID]?.zoom ?? currentZoomVal(a);
  const effectiveJcsaVal = (a: Agent) => pendingAgentEdits[a.AgentID]?.jcsa ?? currentJcsaVal(a);

  const zoomDirty = (a: Agent) => effectiveZoomVal(a) !== currentZoomVal(a);
  const jcsaDirty = (a: Agent) => effectiveJcsaVal(a) !== currentJcsaVal(a);
  const agentDirty = (a: Agent) => zoomDirty(a) || jcsaDirty(a);
  const pendingAgentCount = agents.filter(agentDirty).length;

  // Stage a dropdown change without persisting it; the Save button writes all staged rows.
  const stageZoom = (agent: Agent, value: string) =>
    setPendingAgentEdits(prev => ({ ...prev, [agent.AgentID]: { ...prev[agent.AgentID], zoom: value } }));
  const stageJcsa = (agent: Agent, value: string) =>
    setPendingAgentEdits(prev => ({ ...prev, [agent.AgentID]: { ...prev[agent.AgentID], jcsa: value } }));

  const handleDiscardAgentEdits = () => setPendingAgentEdits({});

  // Persist every changed row in one go: unpair/pair Zoom and set/unassign the JCSA employee
  // for each dirty column, then clear the staged edits and refresh.
  const handleSaveAgents = async () => {
    const ops: Promise<unknown>[] = [];
    for (const a of agents) {
      if (zoomDirty(a)) {
        const userId = effectiveZoomVal(a);
        if (userId === '') ops.push(setAgentZoom(a.AgentID, {}));
        else {
          const zoom = zoomUsers.find(z => z.UserId === userId);
          if (zoom) ops.push(setAgentZoom(a.AgentID, { ZoomUserId: zoom.UserId, ZoomEmail: zoom.Email, ZoomDisplayName: zoom.DisplayName }));
        }
      }
      if (jcsaDirty(a)) {
        const v = effectiveJcsaVal(a);
        if (v === '') ops.push(setAgentJcsaEmployee(a.AgentID, null, true));   // explicit unassign
        else ops.push(setAgentJcsaEmployee(a.AgentID, Number(v), false));      // manual override
      }
    }
    if (ops.length === 0) return;
    setSavingAgents(true);
    try {
      await Promise.all(ops);
      setPendingAgentEdits({});
      await Promise.all([refreshAgents(), refreshZoomUsers()]);
    } finally {
      setSavingAgents(false);
    }
  };

  const handleAssignCall = async (call: Call) => {
    const agentId = assignSelections[call.CallID];
    if (!agentId) return;
    await assignCall(call.CallID, agentId);
    await Promise.all([refreshUnmapped(), refreshCalls()]);
    setAssignSelections(prev => { const next = { ...prev }; delete next[call.CallID]; return next; });
  };

  const handleSimulate = async () => {
    setSimBusy(true);
    try {
      const selectedAgent = simAgentId !== '' ? agents.find(a => a.AgentID === simAgentId) : undefined;
      await simulateCall({
        CallerNumber: simCallerNumber || undefined,
        ZoomUserId: selectedAgent?.ZoomUserId || undefined,
        Missed: simMissed,
      });
      await Promise.all([refreshUnmapped(), refreshCalls(), refreshMetrics()]);
    } finally {
      setSimBusy(false);
    }
  };

  const handleVerifyExtension = async () => {
    const ext = extensionInput.trim();
    if (!ext) return;
    setPreviewing(true);
    setPreview(null);
    setPreviewError(null);
    try {
      setPreview(await previewZoomQueue(ext));
    } catch (e: any) {
      setPreviewError(e?.response?.data?.error ?? 'Could not reach Zoom to verify the extension.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsMsg(null);
    try {
      const updated = await updateSettings({ ZoomCallQueueExtension: extensionInput.trim() || undefined });
      setSettings(updated);
      setExtensionInput(updated.ZoomCallQueueExtension ?? '');
      setSettingsMsg('Saved.');
    } catch {
      setSettingsMsg('Save failed.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Full 0–23 hour axis so the chart reads as a day even when only a few hours have misses.
  // Labels are 12-hour AM/PM, compact ("12a" … "11p") so all 24 ticks fit the axis.
  const fmtHour12 = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`;
  const hourData = Array.from({ length: 24 }, (_, h) => ({
    hour: fmtHour12(h),
    missed: metrics?.MissedByHour.find(x => x.Hour === h)?.MissedCount ?? 0,
  }));
  // "3:50" from seconds; dash while durations are NULL upstream (R-A1).
  const fmtDur = (seconds?: number | null) => {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const s = Math.round(seconds);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const agentData = (metrics?.PerAgent ?? []).map(a => ({
    name: a.DisplayName ?? String(a.AgentID),
    answered: a.AnsweredCount,
    avgDur: a.AvgDurationSeconds ?? null,
  }));
  // Per-day answered (green) stacked under missed (red). Dates are US Eastern calendar
  // days from the API, serialized as date-only midnights — label them with timeZone:'UTC'
  // so the viewer's own timezone can't shift the bucket to a neighboring day.
  const dayData = (metrics?.ByDay ?? []).map(d => ({
    day: new Date(d.Date).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' }),
    answered: d.AnsweredCount,
    missed: d.MissedCount,
  }));
  const abandonRate = (() => {
    if (!metrics) return '—';
    const total = metrics.AnsweredCount + metrics.MissedCount;
    return total === 0 ? '—' : `${Math.round((metrics.MissedCount / total) * 100)}%`;
  })();

  // Category usage in the selected range. Counts sum to category assignments
  // (a call can carry multiple categories). The pie shows each category's share.
  const categoryData = (metrics?.ByCategory ?? []).map(c => ({
    name: `${c.Code} — ${c.Name}`,
    code: c.Code,
    count: c.CallCount,
  }));
  const categoryTotal = categoryData.reduce((sum, c) => sum + c.count, 0);
  const categoryPct = (count: number) =>
    categoryTotal === 0 ? '0%' : `${Math.round((count / categoryTotal) * 100)}%`;

  const axisTick = { fill: '#94a3b8', fontSize: 12 };
  const CATEGORY_COLORS = [
    '#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed',
    '#0891b2', '#db2777', '#65a30d', '#9333ea', '#ea580c',
    '#0d9488', '#4f46e5',
  ];

  return (
    <PageLayout navItems={navItems}>
      <h1 className={`${ui.pageTitle} mb-6`}>Communication — Admin</h1>

      <div className="mb-6 flex items-center justify-between gap-3 border-b border-border-default">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id ? 'border-blue-600 text-primary' : 'border-transparent text-muted hover:text-secondary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {(tab === 'calls' || tab === 'metrics') && (
          <button className={`${ui.btnSecondary} mb-1`} onClick={handleManualRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* ── Agents ─────────────────────────────────────────── */}
        {tab === 'agents' && (
          <section className={ui.sectionCard}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className={ui.sectionTitle}>Agents</h2>
              <div className="flex items-center gap-3">
                {pendingAgentCount > 0 && (
                  <span className="text-sm text-muted">
                    {pendingAgentCount} unsaved {pendingAgentCount === 1 ? 'row' : 'rows'}
                  </span>
                )}
                <button
                  className={ui.btnSecondary}
                  onClick={handleDiscardAgentEdits}
                  disabled={savingAgents || pendingAgentCount === 0}
                >
                  Discard
                </button>
                <button
                  className={ui.btnPrimary}
                  onClick={handleSaveAgents}
                  disabled={savingAgents || pendingAgentCount === 0}
                >
                  {savingAgents ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead className={ui.thead}>
                  <tr>
                    <th className={ui.th}>Name (Email)</th>
                    <th className={ui.th}>Zoom Pairing</th>
                    <th className={ui.th}>JCSA Employee</th>
                    <th className={ui.th}>Active</th>
                    <th className={`${ui.th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={ui.tbody}>
                  {(() => {
                    // A Zoom user is taken if any row (staged edits included) is pointing at it,
                    // so a user paired in one row disappears from the others' lists.
                    const takenZoom = new Set(agents.map(effectiveZoomVal).filter(v => v !== ''));
                    return agents.map(a => {
                    const rowZoom = effectiveZoomVal(a);
                    const rowJcsa = effectiveJcsaVal(a);
                    const dirtyRing = ' border-amber-400 ring-1 ring-amber-400';
                    return (
                      <tr key={a.AgentID} className="hover:bg-surface-hover">
                        <td className={ui.td}>{a.DisplayName ?? a.AzureADObjectID}{a.Email ? ` (${a.Email})` : ''}</td>
                        <td className={ui.td}>
                          <select
                            className={ui.control + (zoomDirty(a) ? dirtyRing : '')}
                            value={rowZoom}
                            onChange={e => stageZoom(a, e.target.value)}
                          >
                            <option value="">— Unassigned —</option>
                            {rowZoom !== '' && !zoomUsers.some(z => z.UserId === rowZoom) && (
                              <option value={rowZoom}>
                                {a.ZoomDisplayName ?? rowZoom}{a.ZoomEmail ? ` (${a.ZoomEmail})` : ''}
                              </option>
                            )}
                            {zoomUsers.filter(z => z.UserId === rowZoom || !takenZoom.has(z.UserId)).map(z => (
                              <option key={z.UserId} value={z.UserId}>
                                {z.DisplayName ?? z.UserId}{z.Email ? ` (${z.Email})` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={ui.td}>
                          <select
                            className={ui.control + (jcsaDirty(a) ? dirtyRing : '')}
                            value={rowJcsa}
                            onChange={e => stageJcsa(a, e.target.value)}
                          >
                            <option value="">— Unassigned —</option>
                            {jcsaEmployees.map(emp => (
                              <option key={emp.EmployeeID} value={emp.EmployeeID}>
                                {emp.Name || `Employee #${emp.EmployeeID}`}{emp.Email ? ` (${emp.Email})` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={ui.td}>{a.IsActive ? 'Yes' : 'No'}</td>
                        <td className={`${ui.td} text-right`}>
                          {a.IsActive ? (
                            <button className={ui.btnDanger} onClick={async () => { await deactivateAgent(a.AgentID); await refreshAgents(); }}>
                              Deactivate
                            </button>
                          ) : (
                            <button className={ui.btnSecondary} onClick={async () => { await reactivateAgent(a.AgentID); await refreshAgents(); }}>
                              Reactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  });
                  })()}
                  {agents.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted">No agents yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Calls (all + unassigned + dev simulator) ─────────── */}
        {tab === 'calls' && (
          <>
            <section className={ui.sectionCard}>
              <h2 className={`${ui.sectionTitle} mb-4`}>All Calls</h2>
              <div className={ui.tableWrap}>
                <table className={ui.table}>
                  <thead className={ui.thead}>
                    <tr><th className={ui.th}>Time</th><th className={ui.th}>Agent</th><th className={ui.th}>Account</th><th className={ui.th}>Status</th></tr>
                  </thead>
                  <tbody className={ui.tbody}>
                    {pagedCalls.map(c => (
                      <tr key={c.CallID} className="hover:bg-surface-hover">
                        <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeLocal(c.CallTimeUtc)}</td>
                        <td className={ui.td}>{c.AgentName ?? (c.AgentID ? `#${c.AgentID}` : <span className="text-muted">—</span>)}</td>
                        <td className={ui.td}>{c.AccountNumber || <span className="text-muted">—</span>}</td>
                        <td className={ui.td}><StatusBadge status={c.Status} /></td>
                      </tr>
                    ))}
                    {calls.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">No calls yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pager page={callsPage} total={calls.length} pageSize={CALLS_PAGE_SIZE} onPage={setCallsPage} />
            </section>

            <section className={ui.sectionCard}>
              <h2 className={`${ui.sectionTitle} mb-1`}>Unassigned Calls ({unmapped.length})</h2>
              <p className="mb-4 text-sm text-muted">Calls whose Zoom user has no agent mapping.</p>
              <div className={ui.tableWrap}>
                <table className={ui.table}>
                  <thead className={ui.thead}>
                    <tr><th className={ui.th}>Time</th><th className={ui.th}>Caller</th><th className={ui.th}>Account</th><th className={ui.th}>ZoomCallId</th><th className={ui.th}>Assign To</th></tr>
                  </thead>
                  <tbody className={ui.tbody}>
                    {pagedUnmapped.map(c => (
                      <tr key={c.CallID} className="hover:bg-surface-hover">
                        <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeLocal(c.CallTimeUtc)}</td>
                        <td className={ui.td}>{c.CallerNumber ? formatPhoneNumber(c.CallerNumber) : <span className="text-muted">—</span>}</td>
                        <td className={ui.td}>{c.AccountNumber}</td>
                        <td className={`${ui.td} font-mono text-xs`}>{c.ZoomCallId}</td>
                        <td className={ui.td}>
                          <div className="flex items-center gap-2">
                            <select
                              className={ui.control}
                              value={assignSelections[c.CallID] ?? ''}
                              onChange={e => setAssignSelections(prev => ({ ...prev, [c.CallID]: Number(e.target.value) }))}
                            >
                              <option value="">— select agent —</option>
                              {activeAgents.map(a => (
                                <option key={a.AgentID} value={a.AgentID}>
                                  {a.DisplayName ?? a.AzureADObjectID}{a.Email ? ` (${a.Email})` : ''}
                                </option>
                              ))}
                            </select>
                            <button className={ui.btnPrimary} disabled={!assignSelections[c.CallID]} onClick={() => handleAssignCall(c)}>
                              Assign
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {unmapped.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted">No unassigned calls.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pager page={unmappedPage} total={unmapped.length} pageSize={CALLS_PAGE_SIZE} onPage={setUnmappedPage} />
            </section>

            {me?.Environment && me.Environment !== 'Production' && (
              <section className={`${ui.sectionCard} border-dashed`}>
                <h2 className={ui.sectionTitle}>
                  Dev Simulator <span className="ml-1 text-xs font-normal text-muted">(non-production tool)</span>
                </h2>
                <p className="mb-4 mt-1 text-sm text-muted">
                  Simulate an inbound call for testing. This panel is only visible in non-production environments.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className={ui.label}>Caller #</label>
                    <input
                      type="text"
                      className={ui.control}
                      placeholder="e.g. +15551234567 (optional)"
                      value={simCallerNumber}
                      onChange={e => setSimCallerNumber(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={ui.label}>Route to agent</label>
                    <select className={ui.control} value={simAgentId} onChange={e => setSimAgentId(e.target.value === '' ? '' : Number(e.target.value))}>
                      <option value="">— unassigned —</option>
                      {activeAgents.filter(a => a.ZoomUserId).map(a => (
                        <option key={a.AgentID} value={a.AgentID}>
                          {a.DisplayName ?? a.AzureADObjectID}{a.Email ? ` (${a.Email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-secondary">
                    <input type="checkbox" className={ui.checkbox} checked={simMissed} onChange={e => setSimMissed(e.target.checked)} />
                    <span>Missed (nobody answered)</span>
                  </label>
                  <button className={ui.btnPrimary} onClick={handleSimulate} disabled={simBusy}>
                    {simBusy ? 'Simulating…' : simMissed ? 'Simulate missed call' : 'Simulate inbound call'}
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {/* ── Categories ───────────────────────────────────────── */}
        {tab === 'categories' && (
          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-4`}>Categories</h2>
            <ul className="divide-y divide-border-default">
              {categories.map(c => (
                <li key={c.CategoryID} className="flex items-center justify-between py-2 text-sm text-secondary">
                  <span>
                    <span className="font-medium text-primary">{c.Code}</span> — {c.Name}
                    {!c.IsActive && <span className="ml-2 text-muted">(inactive)</span>}
                  </span>
                  {c.IsActive && (
                    <button className={ui.btnDanger} onClick={async () => { await deactivateCategory(c.CategoryID); setCategories(await getCategories()); }}>
                      Deactivate
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Metrics (missed calls + graphs) ──────────────────── */}
        {tab === 'metrics' && (
          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-1`}>Missed Calls</h2>
            <p className="mb-4 text-sm text-muted">Customer-care queue calls. All times US Eastern (ET); weeks run Sunday–Saturday.</p>

            <div className="mb-6 flex flex-wrap items-end gap-3">
              <div>
                <label className={ui.label}>Date range</label>
                <select
                  className={ui.control}
                  value={metricsPreset}
                  onChange={e => setMetricsPreset(e.target.value as MetricsPreset)}
                >
                  {METRICS_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              {metricsPreset === 'custom' && (
                <>
                  <div>
                    <label className={ui.label}>Start date</label>
                    <input
                      type="date"
                      className={ui.control}
                      value={customFrom}
                      max={customTo || undefined}
                      onChange={e => setCustomFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={ui.label}>End date</label>
                    <input
                      type="date"
                      className={ui.control}
                      value={customTo}
                      min={customFrom || undefined}
                      onChange={e => setCustomTo(e.target.value)}
                    />
                  </div>
                </>
              )}
              <span className="pb-2 text-sm text-muted">
                {metricsRange
                  ? `${fmtRangeDate(metricsRange.from)} – ${fmtRangeDate(metricsRange.to)}`
                  : 'Pick a start and end date.'}
              </span>
            </div>
            {metrics && (
              <>
                <div className="mb-6 flex flex-wrap gap-4">
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Total Calls</div>
                    <div className="text-2xl font-bold text-primary">{metrics.AnsweredCount + metrics.MissedCount}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Answered</div>
                    <div className="text-2xl font-bold text-primary">{metrics.AnsweredCount}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Missed</div>
                    <div className="text-2xl font-bold text-red-600">{metrics.MissedCount}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Abandon rate</div>
                    <div className="text-2xl font-bold text-primary">{abandonRate}</div>
                  </div>
                  <div className="rounded-md border border-border-default bg-surface px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted">Avg Length of Call</div>
                    <div className="text-2xl font-bold text-primary">{fmtDur(metrics.AvgDurationSeconds)}</div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="mb-2 text-sm font-semibold text-secondary">Calls per day (answered vs. missed, ET)</h3>
                  <div style={{ height: 300 }}>
                    {dayData.length === 0 ? (
                      <div className="flex h-full items-center justify-center text-sm text-muted">No calls in range.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dayData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                          <XAxis dataKey="day" tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <Tooltip cursor={{ fill: 'rgba(148,163,184,0.15)' }} />
                          <Legend />
                          <Bar dataKey="answered" name="Answered" stackId="calls" fill="#16a34a" />
                          <Bar dataKey="missed" name="Missed" stackId="calls" fill="#dc2626" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Missed by hour of day (ET)</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hourData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                          <XAxis dataKey="hour" tick={axisTick} interval={1} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                          <Tooltip
                            cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                            labelFormatter={(h) => `${h}m ET`}
                            formatter={(v) => [String(v), 'Missed']}
                          />
                          <Bar dataKey="missed" fill="#dc2626" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Answered by agent</h3>
                    <div style={{ height: 260 }}>
                      {agentData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-muted">No answered calls.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={agentData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <YAxis type="category" dataKey="name" width={120} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <Tooltip
                              cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                              formatter={(v: any, _n: any, item: any) =>
                                [`${v} · avg call ${fmtDur(item?.payload?.avgDur)}`, 'Answered']}
                            />
                            <Bar dataKey="answered" fill="#2563eb" radius={[0, 3, 3, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Calls by category (count)</h3>
                    {/* Height scales with the category count so every row fits; interval={0}
                        stops recharts from skipping Y-axis labels when space gets tight. */}
                    <div style={{ height: categoryData.length === 0 ? 260 : Math.max(260, categoryData.length * 32 + 40) }}>
                      {categoryData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-muted">No categorized calls in range.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={categoryData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <YAxis type="category" dataKey="name" width={180} interval={0} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                            <Tooltip cursor={{ fill: 'rgba(148,163,184,0.15)' }} formatter={(v) => [String(v), 'Calls']} />
                            <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                              {categoryData.map((c, i) => (
                                <Cell key={c.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-secondary">Calls by category (%)</h3>
                    <div style={{ height: 260 }}>
                      {categoryData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-muted">No categorized calls in range.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                            <Pie
                              data={categoryData}
                              dataKey="count"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius="80%"
                              label={(entry: any) => `${entry.code} ${categoryPct(entry.count)}`}
                              labelLine={false}
                            >
                              {categoryData.map((c, i) => (
                                <Cell key={c.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any, n: any) => [`${v} calls (${categoryPct(Number(v))})`, n]} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted">
                      Percentages are of category assignments — a call with multiple categories counts once per category.
                    </p>
                  </div>
                </div>

                <h3 className="mb-2 mt-6 text-sm font-semibold text-secondary">Recent missed calls</h3>
                <div className={ui.tableWrap}>
                  <table className={ui.table}>
                    <thead className={ui.thead}>
                      <tr><th className={ui.th}>Time</th><th className={ui.th}>Caller</th><th className={ui.th}>Account</th><th className={ui.th}>Student</th><th className={ui.th}>Center</th></tr>
                    </thead>
                    <tbody className={ui.tbody}>
                      {metrics.RecentMissed.map(m => (
                        <tr key={m.CallID} className="hover:bg-surface-hover">
                          <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeET(m.CallTimeUtc)}</td>
                          <td className={ui.td}>{m.CallerNumber ? formatPhoneNumber(m.CallerNumber) : <span className="text-muted">—</span>}</td>
                          <td className={ui.td}>{m.AccountNumber || <span className="text-muted">—</span>}</td>
                          <td className={ui.td}>{m.StudentName || <span className="text-muted">—</span>}</td>
                          <td className={ui.td}>{m.SchoolName || <span className="text-muted">—</span>}</td>
                        </tr>
                      ))}
                      {metrics.RecentMissed.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted">No missed calls.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {/* ── Time Tracking (Workforce schema, read-only) ──────── */}
        {tab === 'timetracking' && <TimeTrackingAdmin />}

        {/* ── Settings ─────────────────────────────────────────── */}
        {tab === 'settings' && (
          <section className={ui.sectionCard}>
            <h2 className={`${ui.sectionTitle} mb-1`}>Customer Care Call Queue</h2>
            <p className="mb-4 text-sm text-muted">
              The dialable extension of the Zoom call queue for Customer Care. It controls both which
              inbound calls get logged and which Zoom users appear in the agent-pairing dropdown.
              Changes take effect immediately.
            </p>

            {settings && (
              <p className="mb-4 text-xs text-muted">
                Current:{' '}
                <span className="font-medium text-secondary">
                  {settings.ZoomCallQueueExtension || '(none)'}
                </span>{' '}
                <span className="rounded bg-surface px-1.5 py-0.5">
                  source: {settings.ZoomCallQueueExtensionSource}
                </span>
                {settings.ZoomCallQueueExtensionSource === 'Database' && settings.UpdatedBy && (
                  <> · updated by {settings.UpdatedBy}</>
                )}
              </p>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className={ui.label}>Queue extension</label>
                <input
                  type="text"
                  className={ui.control}
                  placeholder="e.g. 9003 (test) / 1003 (prod)"
                  value={extensionInput}
                  onChange={e => { setExtensionInput(e.target.value); setSettingsMsg(null); setPreview(null); setPreviewError(null); }}
                />
              </div>
              <button
                className={ui.btnSecondary}
                onClick={handleVerifyExtension}
                disabled={previewing || !extensionInput.trim()}
              >
                {previewing ? 'Verifying…' : 'Verify against Zoom'}
              </button>
              <button className={ui.btnPrimary} onClick={handleSaveSettings} disabled={settingsSaving}>
                {settingsSaving ? 'Saving…' : 'Save'}
              </button>
              {settingsMsg && <span className="text-sm text-muted">{settingsMsg}</span>}
            </div>

            {preview && (
              <div className="mt-4 rounded-md border border-border-default bg-surface px-4 py-3 text-sm">
                {preview.Found ? (
                  <span className="text-secondary">
                    ✓ Matched queue <span className="font-medium text-primary">{preview.QueueName || preview.QueueId}</span>{' '}
                    with <span className="font-medium text-primary">{preview.MemberCount}</span> member{preview.MemberCount === 1 ? '' : 's'}.
                  </span>
                ) : (
                  <span className="text-red-600">No Zoom call queue has extension {preview.Extension}.</span>
                )}
              </div>
            )}
            {previewError && (
              <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
                {previewError}
              </div>
            )}
          </section>
        )}
      </div>
    </PageLayout>
  );
};
